/**
 * Reading the `proposals` list from `POST /nlt/workflow-patch`.
 *
 * Every operation the agent proposed comes back, including the ones that were
 * dropped, and all of them are meant to be shown: filtering to `applied` would hide
 * that the agent tried something else and why it did not land — which is exactly what
 * a person needs in order to rephrase the request.
 */

import type { NltWorkflowProjection, NltWorkflowProposal } from '../types/nltWorkflow';

// ─── JSON pointer (RFC 6901) ────────────────────────────────────────────────

function unescapePointerToken(token: string): string {
  return token.replace(/~1/g, '/').replace(/~0/g, '~');
}

/** `undefined` when the pointer does not resolve — a missing address is a real answer. */
export function resolveJsonPointer(root: unknown, pointer: string): unknown {
  if (!pointer || pointer === '/') return root;
  const tokens = pointer.replace(/^\//, '').split('/').map(unescapePointerToken);
  let current: unknown = root;
  for (const token of tokens) {
    if (current == null) return undefined;
    if (Array.isArray(current)) {
      const index = Number(token);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) return undefined;
      current = current[index];
      continue;
    }
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[token];
  }
  return current;
}

/** The state or global section a proposal addresses, inside a projection. */
function resolveProposalRoot(projection: NltWorkflowProjection | null, proposal: NltWorkflowProposal): unknown {
  if (!projection) return undefined;
  if (proposal.section === 'global') return projection.globals?.[proposal.ident];
  return projection.states?.[proposal.ident];
}

/**
 * Адрес вида `state:greeting/agents/front/prompt` — это «стейт greeting, агент front,
 * поле prompt», но одной строкой он читается как путь в файловой системе. Разбираем
 * на части, чтобы показать, где правка, а не заставлять расшифровывать её глазами.
 */
export interface ProposalWhere {
  /** `state` или `global` — по префиксу адреса. */
  scope: 'state' | 'global' | 'unknown';
  /** Идентификатор стейта или имя глобальной секции. */
  owner: string;
  /** Остаток пути: роль, секция, поле. */
  path: string[];
  /** Последний сегмент — само правленое поле. */
  field: string;
}

export function describeProposalAddress(address: string): ProposalWhere {
  const raw = String(address || '').trim();
  const colon = raw.indexOf(':');
  const prefix = colon > 0 ? raw.slice(0, colon) : '';
  const rest = colon > 0 ? raw.slice(colon + 1) : raw;
  const segments = rest.split('/').filter(Boolean);
  const scope: ProposalWhere['scope'] = prefix === 'state' ? 'state' : prefix === 'global' ? 'global' : 'unknown';
  const owner = scope === 'unknown' ? '' : segments[0] ?? '';
  const path = scope === 'unknown' ? segments : segments.slice(1);
  return { scope, owner, path, field: segments[segments.length - 1] ?? raw };
}

export function formatProposalValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// ─── Outcome ────────────────────────────────────────────────────────────────

export type ProposalOutcome = 'applied' | 'rejected-by-ml' | 'duplicate' | 'superseded' | 'apply-failed' | 'skipped';

export interface ProposalView {
  proposal: NltWorkflowProposal;
  outcome: ProposalOutcome;
  /** Short human label for the badge. */
  outcomeLabel: string;
  /** Why it did not land, when it did not. */
  reason: string;
  /** Global sections are read-only on this connector — see WORKFLOW_API.md §"Права записи". */
  writable: boolean;
  oldValue: string;
  newValue: string;
  /** Both sides are plain strings AND differ, so the text diff has something to draw. */
  isTextDiff: boolean;
  /**
   * Значение по адресу действительно изменилось — после приведения к тексту.
   *
   * Шире `isTextDiff`: объекты и списки тоже сравниваются, потому что
   * `formatProposalValue` печатает их многострочным JSON, а построчный diff по нему
   * читается ровно так же. Раньше такие правки показывались двумя блоками «до» и
   * «после», и найти в них изменившийся ключ можно было только глазами.
   */
  hasValueDiff: boolean;
  /** Разобранный адрес: где именно правка, а не одна строка с косыми чертами. */
  where: ProposalWhere;
  /** Адрес нашёлся в отправленном воркфлоу. Иначе показывать «до/после» бессмысленно. */
  resolved: boolean;
}

const OUTCOME_LABELS: Record<ProposalOutcome, string> = {
  applied: 'applied',
  'rejected-by-ml': 'rejected by ML',
  duplicate: 'duplicate',
  superseded: 'superseded',
  'apply-failed': 'not applied',
  skipped: 'not selected',
};

function classify(proposal: NltWorkflowProposal): ProposalOutcome {
  if (proposal.applied) return 'applied';
  if (proposal.apply_error) return 'apply-failed';
  if (String(proposal.status).toLowerCase() === 'error') return 'rejected-by-ml';
  const skipped = String(proposal.skipped || '').toLowerCase();
  if (skipped.includes('duplicate')) return 'duplicate';
  if (skipped.includes('superseded')) return 'superseded';
  if (skipped.includes('rejected')) return 'rejected-by-ml';
  return 'skipped';
}

function reasonFor(proposal: NltWorkflowProposal, outcome: ProposalOutcome): string {
  switch (outcome) {
    case 'applied':
      return '';
    case 'rejected-by-ml':
      return proposal.error || proposal.skipped || 'ML rejected this operation.';
    case 'apply-failed':
      return (
        proposal.apply_error ||
        'The address was not found in the workflow that was sent — the graph most likely moved between reading and this turn.'
      );
    case 'duplicate':
      return proposal.skipped || 'The same operation on the same address was already proposed in this turn.';
    case 'superseded':
      return proposal.skipped || 'A later operation on the same address won.';
    default:
      return proposal.skipped || proposal.error || '';
  }
}

export function buildProposalViews(
  proposals: NltWorkflowProposal[],
  sent: NltWorkflowProjection | null,
  proposed: NltWorkflowProjection | null
): ProposalView[] {
  return (proposals || []).map((proposal) => {
    const outcome = classify(proposal);
    const before = resolveJsonPointer(resolveProposalRoot(sent, proposal), proposal.pointer);
    const after = resolveJsonPointer(resolveProposalRoot(proposed, proposal), proposal.pointer);
    const oldValue = formatProposalValue(before);
    const newValue = formatProposalValue(after);
    return {
      proposal,
      outcome,
      outcomeLabel: OUTCOME_LABELS[outcome],
      reason: reasonFor(proposal, outcome),
      writable: proposal.section !== 'global',
      oldValue,
      // Only an applied operation actually changed the document; for the rest the two
      // sides are identical, and pretending otherwise would invent a diff.
      newValue: outcome === 'applied' ? newValue : oldValue,
      // Текстовый diff берём только когда обе стороны — строки и они разные.
      // Иначе NltTextFieldDiffLines не находит изменённых строк и отдаёт null,
      // а обёртка остаётся пустой: снаружи это выглядит как «диффы не показываются».
      isTextDiff:
        outcome === 'applied' &&
        typeof before === 'string' &&
        typeof after === 'string' &&
        before !== after,
      hasValueDiff: outcome === 'applied' && oldValue !== newValue,
      where: describeProposalAddress(proposal.address),
      resolved: before !== undefined,
    };
  });
}

export interface ProposalSummary {
  total: number;
  applied: number;
  appliedStates: number;
  appliedGlobals: number;
  notApplied: number;
}

export function summarizeProposals(views: ProposalView[]): ProposalSummary {
  const applied = views.filter((v) => v.outcome === 'applied');
  return {
    total: views.length,
    applied: applied.length,
    appliedStates: applied.filter((v) => v.writable).length,
    appliedGlobals: applied.filter((v) => !v.writable).length,
    notApplied: views.length - applied.length,
  };
}

/** State ids touched by applied proposals — what the graph edit actually covers. */
export function appliedStateIds(views: ProposalView[]): string[] {
  const ids = new Set<string>();
  views.forEach((v) => {
    if (v.outcome === 'applied' && v.writable && v.proposal.ident) ids.add(v.proposal.ident);
  });
  return Array.from(ids);
}
